// The researcher's panel, drawn in the bench's own style and kept off the
// child's paper: frustration metrics replayed from the edit log, sight
// coverage against the fixture map, the log itself, refused edits, the
// recorder's state and the last error in full. Two fixture tools exercise
// the ladder before the intent route exists: surface a word for doubt, and
// attach alternatives as a `judge` edit whose model is named `lab-fixture`.
import {
  type CompositionToken,
  type EditOp,
  frustrationMetrics,
  overlay,
} from "@bindery/composition";
import type { CaptureState } from "@bindery/speech/capture";
import { Button } from "@bindery/ui/components/ui/button";
import { useState } from "react";
import { Absent, Panel, Readout, Readouts } from "../components/panel";
import { clock } from "../lib/format";
import type { ComposeSession } from "./session";

/** One intent call as the page saw it: outcome and latency, never a word. */
export interface IntentEvent {
  readonly outcome: string;
  readonly ms: number;
  readonly asked: number;
  readonly judged: number;
}

export interface LabPanelProps {
  readonly session: ComposeSession;
  readonly capture: CaptureState;
  readonly level: number;
  readonly mode: string;
  readonly lastError: unknown;
  readonly intentLog: readonly IntentEvent[];
  readonly onSurface: (tokenId: string) => void;
}

const short = (id: string) => id.slice(-6);

const describe = (
  edit: ComposeSession["composition"]["edits"][number],
): string => {
  switch (edit.op) {
    case "insert-dictation":
      return `${String(edit.tokens.length)} tokens at ${String(edit.atIndex)}`;
    case "re-say":
      return `${edit.result.outcome} · ${edit.tokenIds.map(short).join(",")}`;
    case "choose":
    case "type":
      return `${edit.from} → ${edit.to}`;
    case "reject":
      return `${edit.rung} · ${short(edit.tokenId)}`;
    case "surface":
    case "delete":
      return edit.tokenIds.map(short).join(",");
    case "move":
      return `→ ${String(edit.toIndex)}`;
    case "judge":
      return edit.judgements
        .map(
          (j) =>
            `${short(j.tokenId)}: ${j.judgement.alternatives.map((a) => a.word).join("/")}`,
        )
        .join("; ");
    case "describe":
      return edit.result.outcome === "chosen"
        ? `chosen → ${edit.result.to} · scored by ${edit.result.model ?? "nobody"}` +
            (edit.result.generator === undefined
              ? ""
              : ` · proposed by ${edit.result.generator}`)
        : `${edit.result.outcome} · ${short(edit.tokenId)}`;
    default:
      return "tokenId" in edit ? short(edit.tokenId) : "";
  }
};

const words = (tokens: readonly CompositionToken[]) =>
  tokens.flatMap((t) => (t.kind === "word" ? [t] : []));

const pct = (n: number | null) =>
  n === null ? null : `${(n * 100).toFixed(0)}%`;
const num = (n: number | null, digits = 2) =>
  n === null ? null : n.toFixed(digits);

function FixtureTools({ session, onSurface }: LabPanelProps) {
  const options = words(session.composition.tokens);
  const [tokenId, setTokenId] = useState("");
  const chosen = options.find((w) => w.id === tokenId) ?? options[0];
  return (
    <div className="flex flex-col gap-2 border-t pt-3">
      <span className="lab-label">fixture tool (lab only)</span>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="bg-background h-8 rounded-sm border px-2 font-mono text-xs"
          value={chosen?.id ?? ""}
          onChange={(e) => setTokenId(e.target.value)}
          aria-label="Word"
        >
          {options.map((w) => (
            <option key={w.id} value={w.id}>
              {w.text} · {short(w.id)} · {w.resolution}
            </option>
          ))}
        </select>
        <Button
          size="xs"
          variant="outline"
          disabled={chosen === undefined}
          onClick={() => chosen && onSurface(chosen.id)}
        >
          surface for doubt
        </Button>
      </div>
    </div>
  );
}

export function LabPanel(props: LabPanelProps) {
  const { session, capture, level, mode, lastError, intentLog } = props;
  const edits = session.composition.edits;
  const intentByOutcome = intentLog.reduce<Record<string, number>>(
    (acc, e) => ({ ...acc, [e.outcome]: (acc[e.outcome] ?? 0) + 1 }),
    {},
  );
  const intentMs = intentLog.map((e) => e.ms);
  const describeByOutcome = edits.reduce<Record<string, number>>(
    (acc, e) =>
      e.op === "describe"
        ? { ...acc, [e.result.outcome]: (acc[e.result.outcome] ?? 0) + 1 }
        : acc,
    {},
  );
  const metrics = frustrationMetrics(session.composition);
  const cover = overlay(session.composition.tokens, session.sight).coverage;
  const counts = edits.reduce<Partial<Record<EditOp, number>>>(
    (acc, e) => ({ ...acc, [e.op]: (acc[e.op] ?? 0) + 1 }),
    {},
  );
  return (
    <div className="mx-auto grid w-full max-w-5xl gap-4 lg:grid-cols-2">
      <Panel
        index="R1"
        title="Frustration metrics"
        aside={
          <span className="lab-readout text-muted-foreground text-xs">
            replayed from the edit log
          </span>
        }
      >
        {metrics.tag === "err" ? (
          <p className="lab-readout text-xs">
            replay refused at edit {metrics.error.editIndex}:{" "}
            {metrics.error.cause.kind}
          </p>
        ) : (
          <Readouts>
            <Readout label="surfaced words">
              {metrics.value.surfacedWords}
            </Readout>
            <Readout label="mean attempts">
              {num(metrics.value.meanAttempts) ?? <Absent />}
            </Readout>
            <Readout label="median to resolution">
              {metrics.value.medianMsToResolution === null ? (
                <Absent />
              ) : (
                clock(metrics.value.medianMsToResolution)
              )}
            </Readout>
            <Readout label="saved for adult">
              {pct(metrics.value.savedForAdultShare) ?? <Absent />}
            </Readout>
            <Readout label="deletes after failure">
              {metrics.value.deletionsAfterFailedCorrection}
            </Readout>
            <Readout label="substitutions">
              {metrics.value.substitutionsAfterFailedRecognition}
            </Readout>
            <Readout label="per word">
              {metrics.value.words.length === 0 ? (
                <Absent />
              ) : (
                <ul className="m-0 list-none p-0 text-xs">
                  {metrics.value.words.map((w) => (
                    <li key={w.root}>
                      {short(w.root)} · {w.outcome} · {w.attempts} att ·{" "}
                      {w.failedAttempts} failed · {w.reSays} re-says
                      {w.substituted ? " · substituted" : ""}
                    </li>
                  ))}
                </ul>
              )}
            </Readout>
          </Readouts>
        )}
        <div className="border-t pt-3">
          <span className="lab-label">intent check</span>
          <Readouts>
            <Readout label="calls">
              {intentLog.length === 0 ? (
                <Absent />
              ) : (
                Object.entries(intentByOutcome)
                  .map(([k, n]) => `${k} ${String(n)}`)
                  .join(" · ")
              )}
            </Readout>
            <Readout label="asked / judged">
              {intentLog.reduce((n, e) => n + e.asked, 0)} /{" "}
              {intentLog.reduce((n, e) => n + e.judged, 0)}
            </Readout>
            <Readout label="latency">
              {intentMs.length === 0 ? (
                <Absent />
              ) : (
                `mean ${String(Math.round(intentMs.reduce((a, b) => a + b, 0) / intentMs.length))} ms · max ${String(Math.max(...intentMs))} ms`
              )}
            </Readout>
            <Readout label="describe">
              {Object.keys(describeByOutcome).length === 0 ? (
                <Absent />
              ) : (
                Object.entries(describeByOutcome)
                  .map(([k, n]) => `${k} ${String(n)}`)
                  .join(" · ")
              )}
            </Readout>
          </Readouts>
        </div>
        <div className="border-t pt-3">
          <span className="lab-label">sight coverage (fixture map)</span>
          <Readouts>
            <Readout label="words / unresolved">
              {cover.words} / {cover.unresolved}
            </Readout>
            <Readout label="sight or predicted">
              {pct(cover.sightOrPredictedShare) ?? <Absent />}
            </Readout>
            <Readout label="by status">
              {Object.entries(cover.byStatus)
                .filter(([, n]) => n > 0)
                .map(([k, n]) => `${k} ${String(n)}`)
                .join(" · ") || <Absent />}
            </Readout>
          </Readouts>
        </div>
        <FixtureTools {...props} />
      </Panel>

      <Panel
        index="R2"
        title="Edit log"
        aside={
          <span className="lab-readout text-muted-foreground text-xs">
            {edits.length} edits
          </span>
        }
      >
        <Readouts>
          <Readout label="recorder">
            {capture.tag} · level {level.toFixed(2)} · {mode}
          </Readout>
          <Readout label="cursor / focus">
            {session.cursor} /{" "}
            {session.focus === null ? <Absent /> : short(session.focus)}
          </Readout>
          <Readout label="clips held">{session.clips.size}</Readout>
          <Readout label="judgements skipped">
            {session.skippedJudgements.length === 0 ? (
              <Absent />
            ) : (
              session.skippedJudgements.map(short).join(",")
            )}
          </Readout>
          <Readout label="dictionary">
            {session.dictionary.size} answered ·{" "}
            {session.awaitingDictionary.size} waiting
          </Readout>
          <Readout label="typed vs re-said">
            {String((counts.type ?? 0) + (counts["insert-typed"] ?? 0))} typed (
            {String(counts.type ?? 0)} as a fix) ·{" "}
            {String(counts["re-say"] ?? 0)} re-says
          </Readout>
          <Readout label="ops">
            {Object.entries(counts)
              .map(([op, n]) => `${op} ${String(n)}`)
              .join(" · ") || <Absent />}
          </Readout>
        </Readouts>
        <ol className="lab-readout m-0 max-h-72 list-none overflow-y-auto border-t p-0 pt-2 text-xs">
          {edits.length === 0 ? (
            <li>
              <Absent>No edits yet.</Absent>
            </li>
          ) : (
            edits.map((e, i) => (
              <li key={i} className="flex gap-2 py-0.5">
                <span className="text-muted-foreground w-6 text-right">
                  {i + 1}
                </span>
                <span className="w-32 font-medium">{e.op}</span>
                <span className="min-w-0 flex-1 break-words">
                  {describe(e)}
                </span>
              </li>
            ))
          )}
        </ol>
        {session.refusals.length > 0 ? (
          <div className="border-t pt-2">
            <span className="lab-label">refused edits</span>
            <pre className="lab-readout overflow-x-auto text-xs">
              {JSON.stringify(
                session.refusals.map((r) => ({
                  op: r.edit.op,
                  error: r.error,
                })),
                null,
                1,
              )}
            </pre>
          </div>
        ) : null}
        {lastError === null || lastError === undefined ? null : (
          <div className="border-t pt-2">
            <span className="lab-label">last error</span>
            <pre className="lab-readout overflow-x-auto text-xs">
              {JSON.stringify(lastError, null, 1)}
            </pre>
          </div>
        )}
      </Panel>
    </div>
  );
}
